/**
 * THE SEMANTIC ANIMATION COMMAND — the only vocabulary allowed to cross from
 * gameplay into the animation controller.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The simulation decides what a player is DOING (`AnimState`, 6 bits on the
 * wire, already cloned/hashed/serialised). The renderer owns what that LOOKS
 * like (which AnimationClip, at what rate, blended how). Those are two different
 * vocabularies and the moment they are allowed to be the same one, gameplay
 * starts naming clips — and a clip rename, a re-authored set or a second visual
 * mode silently changes what the sim can express. That is the class of bug where
 * "the pass animation stopped playing" turns out to be a string literal in a
 * game-logic file.
 *
 * So there are exactly three layers and each only knows its neighbour:
 *
 *     sim AnimState  →  AnimCommand  →  clip
 *     (authoritative)   (this file)      (playerAnimator.ts, private)
 *
 * NO CLIP NAME MAY APPEAR IN THIS FILE, and no `AnimState` may appear inside the
 * controller. `animationWiring.test.ts` asserts both mechanically.
 *
 * AUTHORITY (the rule this whole wiring exists to enforce)
 * -------------------------------------------------------
 * `AnimState` is AUTHORITATIVE for discrete actions. A pass, a shot, a header, a
 * tackle, a dive happens because the SIM says it happened — the renderer never
 * infers one from the ball or from velocity. Velocity may only REFINE playback:
 * rate, gait selection inside the forward ladder, blend and lean. Before this
 * wiring the renderer derived everything from velocity alone, which is a second
 * state machine racing the first: the sim could strike the ball while the visual
 * played a run cycle, and nothing could tell you which one was wrong.
 */
import { AnimState, ActionKind, actionDef } from '@fg/shared';

/**
 * What gameplay is allowed to ask for.
 *
 * Ordered: sustained states first, one-shots second, goalkeeper last. The
 * numeric values are internal — nothing serialises them, so members may be
 * inserted freely (unlike `AnimState`, which is on the wire).
 */
export const enum AnimCommand {
  // ── sustained: a state the sim HOLDS for as long as it lasts ──
  IDLE = 0,
  WALK,
  JOG,
  RUN,
  SPRINT,
  BACKPEDAL,
  STRAFE_LEFT,
  STRAFE_RIGHT,
  JOCKEY,
  FATIGUED,
  FALL,
  STUMBLE,
  THROW_IN_HOLD,
  GK_READY,
  // ── one-shot: a discrete swing with a contact beat and a recovery ──
  PASS_SHORT,
  PASS_LONG,
  SHOOT,
  VOLLEY,
  BICYCLE_KICK,
  HEADER,
  THROW_IN,
  TACKLE,
  SLIDE,
  GET_UP,
  SKILL,
  CELEBRATE,
  JUMP,
  PUNCH,
  KICK,
  GK_DIVE_LEFT,
  GK_DIVE_RIGHT,
  GK_CATCH,
  GK_PUNCH,
  GK_SMOTHER,
  GK_THROW,
  GK_KICK,
  GK_ROLL_OUT,
  CONTROL_KNEE,
  CONTROL_CHEST,
  /** The keeper bending to scoop a ball off the floor. */
  GK_PICKUP_GROUND,
  /** X+X — the keeper setting the held ball down on the turf. */
  GK_PLACE_BALL,
  /** A carrier turning hard with the ball — the lean and the dropped hip. */
  CARRY_TURN,
  PASS_SHORT_LEFT,
  // ── the touchline ──────────────────────────────────────────────────────
  // Requested by castPlayerVisual from `refSignal`, never by the simulation:
  // there is no coach in `WorldState` and these can no more influence a match
  // than the crowd can. They are appended LAST because the enum's order is
  // iterated exhaustively by the coverage tests.
  COACH_INSTRUCT,
  COACH_APPEAL,
  COACH_APPLAUD,
  COACH_FRUSTRATED,
}

/** Highest member, for exhaustive iteration in tests and the mapping sweep. */
export const ANIM_COMMAND_COUNT = AnimCommand.COACH_FRUSTRATED + 1;

/**
 * Human-readable names, in member order. Same pattern as the sim's
 * `ACTION_KIND_NAMES` — an enum whose values only ever appear in a log as an
 * integer is an enum nobody can debug.
 */
export const ANIM_COMMAND_NAMES: readonly string[] = [
  'IDLE', 'WALK', 'JOG', 'RUN', 'SPRINT', 'BACKPEDAL', 'STRAFE_LEFT', 'STRAFE_RIGHT',
  'JOCKEY', 'FATIGUED', 'FALL', 'STUMBLE', 'THROW_IN_HOLD', 'GK_READY',
  'PASS_SHORT', 'PASS_LONG', 'SHOOT', 'VOLLEY', 'BICYCLE_KICK', 'HEADER', 'THROW_IN',
  'TACKLE', 'SLIDE', 'GET_UP', 'SKILL', 'CELEBRATE', 'JUMP', 'PUNCH', 'KICK',
  'GK_DIVE_LEFT', 'GK_DIVE_RIGHT', 'GK_CATCH', 'GK_PUNCH', 'GK_SMOTHER',
  'GK_THROW', 'GK_KICK', 'GK_ROLL_OUT', 'CONTROL_KNEE', 'CONTROL_CHEST',
  'GK_PICKUP_GROUND', 'GK_PLACE_BALL', 'CARRY_TURN',
  'PASS_SHORT_LEFT',
  // The touchline four were appended to the enum on 2026-08-15 without names,
  // so `animCommandName` printed "AnimCommand(40)" and the exhaustive sweeps
  // failed on the count. Named 2026-08-16.
  'COACH_INSTRUCT', 'COACH_APPEAL', 'COACH_APPLAUD', 'COACH_FRUSTRATED',
];

export function animCommandName(cmd: AnimCommand): string {
  return ANIM_COMMAND_NAMES[cmd] ?? `AnimCommand(${cmd})`;
}

/**
 * THE MAPPING TABLE. Every `AnimState` member, explicitly.
 *
 * `Record<AnimState, AnimCommand>` is not decoration: it makes the compiler
 * reject this file the day someone appends a member to `AnimState` without
 * deciding what it looks like. That is the whole guarantee — the alternative is
 * a `switch` with a `default`, which is exactly how `Stumble` shipped as an
 * invisible state (see its comment in types.ts: a barge that shoved the player
 * with nothing on screen).
 *
 * Notes on the non-obvious rows:
 *   - `SlideRecover` → GET_UP. It is not "the end of a slide": the sim reuses it
 *     as the get-up for every knockdown too (see stepAnim's KNOCKDOWN_GETUP_S
 *     branch and getup-continuity.test.ts, which asserts it IS the get-up
 *     phase). One state, one command.
 *   - `Throw` → THROW_IN (the Law 15 two-handed release) while `ThrowInHold` is
 *     the sustained raised-ball pose that precedes it. Two states, two commands,
 *     because they are different body shapes.
 *   - `GkThrow`/`GkRollOut` stay distinct for the same reason (overarm hurl vs
 *     bent-at-the-waist bowl) — collapsing them is what made "he can also throw"
 *     invisible on screen in the first place.
 */
export const ANIM_COMMAND_FOR_STATE: Readonly<Record<AnimState, AnimCommand>> = {
  [AnimState.Idle]: AnimCommand.IDLE,
  [AnimState.Walk]: AnimCommand.WALK,
  [AnimState.Jog]: AnimCommand.JOG,
  [AnimState.Run]: AnimCommand.RUN,
  [AnimState.Sprint]: AnimCommand.SPRINT,
  [AnimState.StrafeLeft]: AnimCommand.STRAFE_LEFT,
  [AnimState.StrafeRight]: AnimCommand.STRAFE_RIGHT,
  [AnimState.Pass]: AnimCommand.PASS_SHORT,
  [AnimState.Shoot]: AnimCommand.SHOOT,
  [AnimState.Volley]: AnimCommand.VOLLEY,
  [AnimState.Header]: AnimCommand.HEADER,
  [AnimState.StandingTackle]: AnimCommand.TACKLE,
  [AnimState.SlideTackle]: AnimCommand.SLIDE,
  [AnimState.SlideRecover]: AnimCommand.GET_UP,
  [AnimState.Jockey]: AnimCommand.JOCKEY,
  [AnimState.Celebrate]: AnimCommand.CELEBRATE,
  [AnimState.Skill]: AnimCommand.SKILL,
  [AnimState.Throw]: AnimCommand.THROW_IN,
  [AnimState.GkIdle]: AnimCommand.GK_READY,
  [AnimState.GkDiveLeft]: AnimCommand.GK_DIVE_LEFT,
  [AnimState.GkDiveRight]: AnimCommand.GK_DIVE_RIGHT,
  [AnimState.GkCatch]: AnimCommand.GK_CATCH,
  [AnimState.GkPunch]: AnimCommand.GK_PUNCH,
  [AnimState.GkThrow]: AnimCommand.GK_THROW,
  [AnimState.GkKick]: AnimCommand.GK_KICK,
  [AnimState.Fallen]: AnimCommand.FALL,
  [AnimState.Fatigued]: AnimCommand.FATIGUED,
  [AnimState.Punch]: AnimCommand.PUNCH,
  [AnimState.Kick]: AnimCommand.KICK,
  [AnimState.Jump]: AnimCommand.JUMP,
  [AnimState.BicycleKick]: AnimCommand.BICYCLE_KICK,
  [AnimState.Stumble]: AnimCommand.STUMBLE,
  [AnimState.GkRollOut]: AnimCommand.GK_ROLL_OUT,
  [AnimState.GkSmother]: AnimCommand.GK_SMOTHER,
  [AnimState.ThrowInHold]: AnimCommand.THROW_IN_HOLD,
  [AnimState.Backpedal]: AnimCommand.BACKPEDAL,
  // A low-charge ground shot — the knock-ahead a surrounded carrier taps past
  // the crowd. The full strike would lie about the intensity, so it borrows
  // the simple in-stride kick. Many states to one command is the table's
  // design (SlideRecover reuses GET_UP the same way).
  [AnimState.ShootLight]: AnimCommand.KICK,
  // Two surfaces, two clips: the knee lifts and drops it to the boot; the
  // chest cushions and lets it fall. Collapsing them onto one command is the
  // aliasing this table exists to refuse.
  [AnimState.KneeControl]: AnimCommand.CONTROL_KNEE,
  [AnimState.ChestControl]: AnimCommand.CONTROL_CHEST,
  [AnimState.GkPickupGround]: AnimCommand.GK_PICKUP_GROUND,
  [AnimState.GkPlaceBall]: AnimCommand.GK_PLACE_BALL,
  [AnimState.CarryTurn]: AnimCommand.CARRY_TURN,
  // A pass aimed to the player's right - struck with the LEFT foot, the
  // mirror of the ordinary right-footed swing. Two states, two commands,
  // because they are two different body shapes (the Throw/ThrowInHold rule).
  [AnimState.PassLeftFooted]: AnimCommand.PASS_SHORT_LEFT,
};

/**
 * Commands NO `AnimState` can currently produce.
 *
 * Declared because the owner's command vocabulary requires them and because the
 * clip authors are working to them, but nothing in the sim distinguishes these
 * yet:
 *   - PASS_LONG: `AnimState.Pass` covers every pass. The sim knows the
 *     difference (`ActionKind.ChargedPass` vs `ShortPass`) but does not publish
 *     it through `anim`, so the renderer must not guess from ball speed — that
 *     is exactly the velocity-derived second state machine this wiring removes.
 *     Closing it is one appended `AnimState` member, not a render change.
 *
 * ✅ GK_SMOTHER IS NO LONGER ONE OF THEM. goalkeeper.ts produced the behaviour
 * and tagged it as a dive, so an authored, bound, tested clip could never
 * appear on screen. `AnimState.GkSmother` (35) closes it — the owner asked for
 * it by name: *"like how real goal keeper do when they get the ball they sleep
 * on the ball"*.
 *
 * Listed rather than omitted so "unreachable" is a stated fact with a reason,
 * not something a reader has to derive from the absence of a row above.
 */
export const UNREACHABLE_COMMANDS: readonly AnimCommand[] = [
  AnimCommand.PASS_LONG,
  // Unreachable FROM THE SIMULATION — no AnimState maps to them, which is what
  // this list means. They are reached: castPlayerVisual requests them off
  // `refSignal` for the touchline staff. Listed so the exhaustive mapping
  // sweep can tell "deliberately not sim-driven" from "forgotten".
  AnimCommand.COACH_INSTRUCT,
  AnimCommand.COACH_APPEAL,
  AnimCommand.COACH_APPLAUD,
  AnimCommand.COACH_FRUSTRATED,
];

/**
 * Sustained vs one-shot. This decides the LIFECYCLE, not the look:
 *   - sustained  → held for as long as the sim holds the state; drives the base
 *                  locomotion layer and never fades itself out.
 *   - one-shot   → plays once, owns the body, fires a contact event, follows
 *                  through, recovers, hands the body back to the gait.
 *
 * `FALL` and `STUMBLE` are SUSTAINED even though they are obviously not gaits:
 * the sim holds them on a timer (`knockdownTimer` / `stumbleTimer`), so running
 * them through the one-shot lifecycle would fade the player back onto his feet
 * while he is still on the floor. `GET_UP` is the one-shot that ends them.
 */
export function isOneShotCommand(cmd: AnimCommand): boolean {
  return cmd >= AnimCommand.PASS_SHORT;
}

/** Convenience inverse — reads better at the call sites that branch on it. */
export function isSustainedCommand(cmd: AnimCommand): boolean {
  return !isOneShotCommand(cmd);
}

/**
 * The forward gait ladder, in order. These are the only commands whose exact
 * clip velocity is allowed to refine (§ "velocity may refine playback rate,
 * gait blend and leaning ONLY"): a sim tick bucket and a render frame disagree
 * about when a player crosses 2.85 m/s, and honouring the bucket edge-for-edge
 * makes the gait flicker on the boundary. The DIRECTIONAL commands below are
 * not in this list — those the sim owns outright.
 */
export const FORWARD_GAIT_COMMANDS: readonly AnimCommand[] = [
  AnimCommand.IDLE, AnimCommand.WALK, AnimCommand.JOG, AnimCommand.RUN, AnimCommand.SPRINT,
];

/** True for a command whose DIRECTION the sim owns and velocity may not override. */
export function isDirectionalGait(cmd: AnimCommand): boolean {
  return (
    cmd === AnimCommand.BACKPEDAL ||
    cmd === AnimCommand.STRAFE_LEFT ||
    cmd === AnimCommand.STRAFE_RIGHT
  );
}

/**
 * WHEN THE BALL IS STRUCK, as a fraction of the action.
 *
 * -1 means "this action never touches the ball" (a celebration, a get-up), and
 * those raise no contact event at all rather than raising one at time zero.
 *
 * SOURCE OF TRUTH: the sim's own `ACTION_DEFS` table, read here rather than
 * copied. `contact-actions.ts` says in as many words "do not scatter timings
 * across files", and a render-side duplicate of 0.42 is precisely how the boot
 * and the ball stop agreeing — the sim already had to retune ShortPass from 20
 * to 28 ticks once because its impulse fired before any clip could plant a foot.
 *
 * TWO REGIMES, stated plainly, because they behave differently and the renderer
 * cannot see which one is live:
 *   - `contactTiming = false` (default/competitive): ball.ts sets the strike
 *     `AnimState` AT impulse resolution, so the state EDGE *is* the contact —
 *     the beat below then sits inside the follow-through.
 *     - `contactTiming = true` (§2 training path): ball.ts sets the state at the
 *     WIND-UP and fires the impulse later, inside `contactWindowStart..End` —
 *     the beat below is then exactly the impulse.
 * Either way the event fires ONCE per action, which is what the token is for.
 * Closing the remaining skew means publishing `actExecId`/`actStartTick` on the
 * render seam; that is a wire change, deliberately not smuggled in here.
 */
export const ACTION_CONTACT_BEAT: Readonly<Record<AnimCommand, number>> = {
  // The scoop's hands reach the ball at f14 of 36 = 0.39 of the clip.
  [AnimCommand.GK_PICKUP_GROUND]: 0.39,
  // The ball touches the turf at f17 of 30 = 0.57.
  [AnimCommand.GK_PLACE_BALL]: 0.57,
  // A turn touches the ball throughout; no single contact beat.
  [AnimCommand.CARRY_TURN]: -1,
  // Sustained states never raise a contact.
  [AnimCommand.IDLE]: -1,
  [AnimCommand.WALK]: -1,
  [AnimCommand.JOG]: -1,
  [AnimCommand.RUN]: -1,
  [AnimCommand.SPRINT]: -1,
  [AnimCommand.BACKPEDAL]: -1,
  [AnimCommand.STRAFE_LEFT]: -1,
  [AnimCommand.STRAFE_RIGHT]: -1,
  [AnimCommand.JOCKEY]: -1,
  [AnimCommand.FATIGUED]: -1,
  [AnimCommand.FALL]: -1,
  [AnimCommand.STUMBLE]: -1,
  [AnimCommand.THROW_IN_HOLD]: -1,
  [AnimCommand.GK_READY]: -1,
  // Footed strikes — straight off the sim's table.
  // ⚠ A SIM PHASE IS NOT A CLIP PHASE. `contactWindowStart` is a fraction of
  // the ACTION (24 ticks = 0.4 s), i.e. the ball leaves at 0.42 x 0.4 =
  // 0.168 s — but used raw it would mark 0.42 of the CLIP. The beat is the
  // sim's release instant expressed in the clip's own duration:
  // 0.168 / 0.600 = 0.28 for the shared quick swing. Recompute it if either
  // the action table or the bound clip changes.
  [AnimCommand.PASS_SHORT]: 0.15,
  [AnimCommand.PASS_LONG]: actionDef(ActionKind.ChargedPass).contactWindowStart,
  // Same rationale as BICYCLE_KICK below: the shot impulse fires ON the
  // trigger tick, and the running-strike clip lands its visible contact at
  // t = 0.10 of its 1.0 s cycle.
  [AnimCommand.SHOOT]: 0.1,
  [AnimCommand.VOLLEY]: actionDef(ActionKind.NormalShot).contactWindowStart,
  // ⚠ NOT the PowerShot window: the bicycle's ball impulse fires ON the
  // trigger tick (`resolveKicks` applies it immediately and floors the
  // kicker), so a sim-table beat would mark an instant 0.6 s after the ball
  // left. The beat is authored against the clip's visible strike — the
  // scissor peaks at t = 0.10 of the 1.5 s arc.
  [AnimCommand.BICYCLE_KICK]: 0.1,
  // Challenges — likewise.
  [AnimCommand.TACKLE]: actionDef(ActionKind.StandingTackle).contactWindowStart,
  [AnimCommand.SLIDE]: actionDef(ActionKind.SlideTackle).contactWindowStart,
  // No ActionKind exists for these, so the beat is AUTHORED against the clip's
  // own strike frame. Each is the point in the swing where the body meets the
  // ball; they are not tunables and should move only if a clip is re-authored.
  [AnimCommand.HEADER]: 0.21,
  [AnimCommand.THROW_IN]: 0.55,
  [AnimCommand.PUNCH]: 0.4,
  // The bound jab's boot passes the ball at 0.168 s of 0.600 s — the same
  // number PASS_SHORT carried while it played that swing. Bound 2026-08-16 for
  // AnimState.ShootLight (the knock-ahead), whose impulse is same-tick; the
  // beat is the visible strike, for the contact event, not a scheduler.
  [AnimCommand.KICK]: 0.28,
  [AnimCommand.GK_DIVE_LEFT]: 0.45,
  [AnimCommand.GK_DIVE_RIGHT]: 0.45,
  [AnimCommand.GK_CATCH]: 0.35,
  [AnimCommand.GK_PUNCH]: 0.4,
  [AnimCommand.GK_SMOTHER]: 0.4,
  [AnimCommand.GK_THROW]: 0.55,
  [AnimCommand.GK_KICK]: 0.45,
  [AnimCommand.GK_ROLL_OUT]: 0.5,
  // One-shots that never touch the ball.
  [AnimCommand.CONTROL_KNEE]: -1,
  [AnimCommand.CONTROL_CHEST]: -1,
  // The mirrored pass keeps the mirrored clip's beat - same release maths as
  // PASS_SHORT (0.168 s of a 0.600 s clip).
  [AnimCommand.PASS_SHORT_LEFT]: 0.15,
  // No ball contact on a touchline gesture — see UNAUTHORED_CONTACT_TIMING.
  [AnimCommand.COACH_INSTRUCT]: -1,
  [AnimCommand.COACH_APPEAL]: -1,
  [AnimCommand.COACH_APPLAUD]: -1,
  [AnimCommand.COACH_FRUSTRATED]: -1,
  [AnimCommand.GET_UP]: -1,
  [AnimCommand.SKILL]: -1,
  [AnimCommand.CELEBRATE]: -1,
  [AnimCommand.JUMP]: -1,
};

/**
 * Thrown when an `AnimState` reaches the renderer that the table above does not
 * cover. It cannot happen from typed sim code — `Record<AnimState, …>` is
 * exhaustive — so in practice it means a corrupt 6-bit wire field or an enum
 * member added without a mapping.
 */
export class UnmappedAnimStateError extends Error {
  constructor(readonly anim: number) {
    super(
      `[anim] AnimState ${anim} has no AnimCommand mapping. Every sim animation ` +
        `state must map explicitly — see ANIM_COMMAND_FOR_STATE in ` +
        `render/player/animationCommand.ts. Silently falling back to a gait is ` +
        `how an entire sim state ships invisible.`,
    );
    this.name = 'UnmappedAnimStateError';
  }
}

/** Dev builds fail loudly; production degrades to IDLE after saying so once. */
function isDev(): boolean {
  return typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV);
}

const unmappedLogged = new Set<number>();

/**
 * `AnimState` → `AnimCommand`. THE seam. Nothing else may read `state.anim`.
 *
 * An unsupported state FAILS LOUDLY in development (throws) because the only
 * cheap moment to notice a state nobody drew is the moment it first appears. In
 * production it logs once and falls back to IDLE, because crashing a live match
 * over an animation is a worse outcome than a wrong pose.
 */
export function commandForAnimState(anim: number): AnimCommand {
  const cmd = (ANIM_COMMAND_FOR_STATE as Record<number, AnimCommand | undefined>)[anim];
  if (cmd === undefined) {
    if (isDev()) throw new UnmappedAnimStateError(anim);
    if (!unmappedLogged.has(anim)) {
      unmappedLogged.add(anim);
      console.error(new UnmappedAnimStateError(anim).message);
    }
    return AnimCommand.IDLE;
  }
  return cmd;
}

/** Test seam: forget which unmapped states have already been reported. */
export function resetUnmappedAnimStateLog(): void {
  unmappedLogged.clear();
}
